import type { BlogPost } from '../../types/blog';

export const post: BlogPost = {
  category: 'guias',
  author: 'Ivan Henrique',
  publishedAt: '2026-05-10',
  coverImage: '/blog/brasileirao-2026.svg',
  tags: ['Brasileirão', 'Série A', 'CBF', 'Bolão', 'Estratégia'],
  i18n: {
    'pt-BR': {
      slug: 'brasileirao-2026',
      title: 'Brasileirão 2026: tabela, sistema de pontos e estratégia de palpites por rodada',
      excerpt:
        'Pontos corridos, 20 clubes, 38 rodadas e 380 jogos: o Brasileirão Série A é o campeonato mais maratona do calendário. Veja como tirar proveito disso no seu bolão.',
      readTimeMinutes: 8,
      coverImageAlt: 'Capa do artigo: Brasileirão 2026 — tabela, pontos e estratégia',
      keywords: ['brasileirão 2026', 'série a', 'tabela brasileirão', 'pontos corridos', 'bolão brasileirão'],
      content: (
        <>
          <p>
            O Campeonato Brasileiro Série A é o torneio nacional mais longo, mais
            denso e — para quem joga bolão — o mais democrático do calendário do
            futebol brasileiro. Diferente da Copa do Brasil, onde uma fase ruim
            pode te eliminar com poucas partidas, o Brasileirão tem 38 rodadas e
            380 jogos. Tem espaço para errar bastante e ainda assim virar campeão
            do bolão.
          </p>
          <p>
            Neste guia, a gente passa por: o formato de pontos corridos, como
            funciona a tabela, o critério de classificação para competições
            internacionais e — o foco central — como adaptar sua estratégia de
            palpites para uma maratona de 9 meses.
          </p>

          <h2>O formato: pontos corridos turno e returno</h2>
          <p>
            O Brasileirão Série A funciona em formato de pontos corridos com 20
            clubes. Cada equipe joga contra todas as outras duas vezes — uma em
            casa, outra fora —, totalizando 38 rodadas. <strong>3 pontos por
            vitória, 1 por empate, 0 por derrota.</strong>
          </p>
          <p>
            O campeão é simplesmente quem somar mais pontos ao fim das 38 rodadas.
            Sem mata-mata, sem decisão em jogo único — é resistência pura.
          </p>

          <h3>Critérios de desempate</h3>
          <p>
            Quando dois times empatam em pontos, a ordem oficial de critérios é:
          </p>
          <ol>
            <li>Maior número de vitórias</li>
            <li>Melhor saldo de gols</li>
            <li>Maior número de gols marcados</li>
            <li>Confronto direto</li>
            <li>Menor número de cartões vermelhos + amarelos</li>
            <li>Sorteio</li>
          </ol>
          <p>
            Saldo de gols, na prática, é o desempate mais usado. Por isso, no
            bolão, vale a pena prestar atenção nos placares grandes — eles podem
            decidir não só rodadas, mas o campeonato inteiro.
          </p>

          <h2>O que está em jogo além do título</h2>
          <p>
            O Brasileirão não decide só o campeão. Ele define também quem joga
            as competições continentais do ano seguinte e quem é rebaixado para
            a Série B:
          </p>
          <ul>
            <li>
              <strong>1º ao 6º colocados:</strong> classificados para a Libertadores
              (4 vagas diretas + 2 para a fase pré-Libertadores)
            </li>
            <li>
              <strong>7º ao 12º colocados:</strong> Sul-Americana
            </li>
            <li>
              <strong>17º ao 20º colocados:</strong> rebaixados para a Série B
            </li>
          </ul>
          <p>
            Essas faixas explicam por que <strong>todos os jogos importam até a
            última rodada</strong> — mesmo times do meio da tabela jogam com fogo
            no rabo, brigando ou por vaga internacional ou pra fugir do
            rebaixamento.
          </p>

          <h2>Estratégia de palpites: maratona, não sprint</h2>
          <p>
            A grande diferença entre o Brasileirão e torneios curtos como Copa do
            Mundo é o fôlego. Você vai palpitar em 380 jogos ao longo de cerca de
            9 meses. Estratégias que funcionam num bolão de Copa <strong>não
            funcionam aqui</strong> — vamos ver por quê.
          </p>

          <h3>1. Não tente acertar todo placar exato</h3>
          <p>
            Em 380 jogos, sua taxa de placar exato vai ficar provavelmente entre
            7% e 12% (mesmo os palpiteiros profissionais raramente passam de 15%
            ao longo de uma temporada inteira). Errar muito é normal. O que faz
            diferença é <strong>manter consistência no acerto do vencedor</strong>
            {' '}— jogadores de bolão de elite acertam o vencedor em torno de 55-60%
            dos jogos.
          </p>
          <p>
            Concentre seu esforço no básico: acertar quem ganha. O placar exato é
            bônus.
          </p>

          <h3>2. Conheça os mandantes fortes e os visitantes fracos</h3>
          <p>
            No futebol brasileiro, a vantagem de jogar em casa é maior que na
            maioria dos países. Equipes do interior, do Nordeste e do Sul têm
            especialmente um peso grande em casa — torcida, viagem cansativa
            para o adversário, condição climática. <strong>Times tradicionais
            do Rio e São Paulo costumam render bem em casa, mas têm campanhas
            bem mais fracas como visitantes</strong>.
          </p>
          <p>
            Anote: <em>quem é forte em casa, é forte em casa — mas pode ser
            comum em visitas</em>. Use isso na hora de palpitar.
          </p>

          <h3>3. Cuidado com a virada de turno</h3>
          <p>
            O Brasileirão tem um ciclo característico: do início até a metade do
            primeiro turno, os times ainda estão se acertando — você vê resultados
            estranhos (zebras, goleadas inesperadas). A partir da 15ª rodada, o
            campeonato &quot;decanta&quot;: os favoritos sobem, os rebaixáveis
            afundam. O segundo turno é mais previsível.
          </p>
          <p>
            Tradução pro palpite: <strong>seja conservador nas 10 primeiras
            rodadas</strong>. Não tente palpitar resultados &quot;criativos&quot;
            no começo do campeonato. Espere os times mostrarem sua identidade.
          </p>

          <h3>4. Atenção ao calendário paralelo</h3>
          <p>
            Times brasileiros disputam vários torneios simultaneamente:
            Brasileirão, Copa do Brasil, Libertadores ou Sul-Americana, e
            estaduais (no primeiro semestre). Isso afeta diretamente os
            palpites: <strong>um time pode usar o time reserva no Brasileirão
            para preservar titulares para um jogo decisivo da Libertadores na
            semana seguinte</strong>.
          </p>
          <p>
            Antes de palpitar, dê uma olhada no calendário dos times grandes da
            sua rodada. Se um deles tem jogo decisivo de Libertadores na semana
            seguinte, é razoável esperar time reserva — e ajustar seu palpite.
          </p>

          <h2>Como criar um bolão do Brasileirão no Bolão Elite</h2>
          <p>
            Acesse a página{' '}
            <a href="/bolao-brasileirao">bolão do Brasileirão</a>, clique em
            &quot;Criar bolão&quot;, defina o nome, a privacidade (privado ou
            público) e o sistema de pontuação. Como a temporada é longa,
            recomendo a Regra B (3 pts placar exato, 1 pt vencedor/empate)
            — equilibra premiação do acerto exato com reconhecimento do
            vencedor correto. Em 38 rodadas, manter algo simples vale mais que
            regra complicada. Quem prefere mais granularidade pode optar pela
            Regra C (10/5/2).
          </p>
          <p>
            Para bolões longos, vale a pena habilitar duas funcionalidades:
          </p>
          <ul>
            <li>
              <strong>Notificações de lembrete:</strong> com 380 jogos, esquecer
              de palpitar é o erro mais frequente. Notificação algumas horas
              antes do jogo evita.
            </li>
            <li>
              <strong>Ranking por rodada além do geral:</strong> em campeonatos
              longos, ter um &quot;destaque da rodada&quot; mantém a galera
              engajada mesmo quem está mal no ranking geral.
            </li>
          </ul>

          <h2>Conclusão</h2>
          <p>
            Bolão do Brasileirão é maratona, não sprint. Não dá pra empolgar nas
            primeiras rodadas e depois cair em frustração na 20ª. Mantenha
            ritmo, palpite com calma, foque no acerto do vencedor e celebre os
            placares exatos quando vierem. No fim do ano, quem palpitar com
            consistência levanta o troféu do bolão.
          </p>
          <p>
            Para mais dicas, veja{' '}
            <a href="/blog">todos os artigos do blog</a> ou comece pelo{' '}
            <a href="/como-jogar">guia completo de como jogar</a>. Termos
            técnicos como &quot;saldo de gols&quot; ou &quot;rebaixamento&quot;
            estão no <a href="/glossario">glossário</a>.
          </p>
        </>
      ),
    },
    en: {
      slug: 'brazilian-league-2026',
      title: 'Brazilian league 2026: standings, points system and matchday-by-matchday prediction strategy',
      excerpt:
        'Round-robin format, 20 clubs, 38 matchdays and 380 matches: the Brazilian league (Brasileirão) Série A is the most marathon-like competition on the calendar. See how to make the most of it in your pool.',
      readTimeMinutes: 8,
      coverImageAlt: 'Article cover: Brazilian league 2026 — standings, points and strategy',
      coverImage: '/blog/brasileirao-2026-en.svg',
      keywords: ['brazilian league 2026', 'série a', 'brasileirão standings', 'round-robin', 'brazilian league pool'],
      content: (
        <>
          <p>
            The Brazilian league Série A is the longest, densest national
            tournament and — for pool players — the most democratic on the
            Brazilian football calendar. Unlike the Copa do Brasil, where one bad
            stage can knock you out after just a few matches, the Brazilian league
            has 38 matchdays and 380 matches. There is room to get plenty wrong
            and still become pool champion.
          </p>
          <p>
            In this guide, we cover: the round-robin format, how the standings
            work, the qualification criteria for international competitions and —
            the central focus — how to adapt your prediction strategy for a
            9-month marathon.
          </p>

          <h2>The format: round-robin, first and second halves</h2>
          <p>
            The Brazilian league Série A runs as a round-robin with 20 clubs.
            Each team plays every other one twice — once at home, once away —
            totalling 38 matchdays. <strong>3 points for a win, 1 for a draw, 0
            for a loss.</strong>
          </p>
          <p>
            The champion is simply whoever totals the most points after the 38
            matchdays. No knockout stage, no single-match decider — it is pure
            endurance.
          </p>

          <h3>Tiebreakers</h3>
          <p>
            When two teams are level on points, the official order of criteria
            is:
          </p>
          <ol>
            <li>Most wins</li>
            <li>Best goal difference</li>
            <li>Most goals scored</li>
            <li>Head-to-head record</li>
            <li>Fewest red + yellow cards</li>
            <li>Drawing of lots</li>
          </ol>
          <p>
            Goal difference, in practice, is the most-used tiebreaker. That is
            why, in a pool, it pays to keep an eye on big scorelines — they can
            decide not just matchdays, but the whole championship.
          </p>

          <h2>What is at stake beyond the title</h2>
          <p>
            The Brazilian league does not only decide the champion. It also
            defines who plays the following year's continental competitions and
            who is relegated to Série B:
          </p>
          <ul>
            <li>
              <strong>1st to 6th place:</strong> qualified for the Copa
              Libertadores (4 direct berths + 2 for the pre-Libertadores stage)
            </li>
            <li>
              <strong>7th to 12th place:</strong> Copa Sudamericana
            </li>
            <li>
              <strong>17th to 20th place:</strong> relegated to Série B
            </li>
          </ul>
          <p>
            These bands explain why <strong>every match matters right up to the
            final matchday</strong> — even mid-table teams play with their backs
            to the wall, fighting either for an international berth or to escape
            relegation.
          </p>

          <h2>Prediction strategy: marathon, not sprint</h2>
          <p>
            The big difference between the Brazilian league and short tournaments
            like the World Cup is stamina. You will make predictions on 380
            matches across roughly 9 months. Strategies that work in a World Cup
            pool <strong>do not work here</strong> — let's see why.
          </p>

          <h3>1. Don't try to nail every exact score</h3>
          <p>
            Across 380 matches, your exact-score rate will probably land between
            7% and 12% (even professional predictors rarely top 15% over an
            entire season). Getting plenty wrong is normal. What makes the
            difference is <strong>staying consistent at picking the
            winner</strong>{' '}— elite pool players get the winner right around
            55-60% of the time.
          </p>
          <p>
            Focus your effort on the basics: getting the winner right. The exact
            score is a bonus.
          </p>

          <h3>2. Know the strong home sides and the weak away sides</h3>
          <p>
            In Brazilian football, home advantage is bigger than in most
            countries. Teams from the interior, the Northeast and the South
            especially carry a lot of weight at home — their crowd, the tiring
            trip for the opponent, the climate. <strong>Traditional teams from
            Rio and São Paulo tend to perform well at home, but have far weaker
            campaigns away</strong>.
          </p>
          <p>
            Take note: <em>whoever is strong at home is strong at home — but can
            be ordinary on the road</em>. Use that when you make your
            predictions.
          </p>

          <h3>3. Watch out for the turn into the second half</h3>
          <p>
            The Brazilian league has a characteristic cycle: from the start until
            the middle of the first half of the season, teams are still settling
            in — you see strange results (upsets, unexpected thrashings). From
            matchday 15 onwards, the championship &quot;settles&quot;: the
            favourites climb, the relegation candidates sink. The second half is
            more predictable.
          </p>
          <p>
            What that means for predictions: <strong>be conservative in the first
            10 matchdays</strong>. Don't try to predict &quot;creative&quot;
            results at the start of the championship. Wait for teams to show
            their identity.
          </p>

          <h3>4. Mind the parallel calendar</h3>
          <p>
            Brazilian teams play several tournaments at the same time: the
            Brazilian league, the Copa do Brasil, the Copa Libertadores or Copa
            Sudamericana, and the state leagues (in the first half of the year).
            This directly affects predictions: <strong>a team may field its
            reserves in the Brazilian league to rest first-choice players for a
            decisive Libertadores match the following week</strong>.
          </p>
          <p>
            Before you predict, take a look at the calendar of the big teams in
            your matchday. If one of them has a decisive Libertadores match the
            following week, it is reasonable to expect a reserve team — and to
            adjust your prediction.
          </p>

          <h2>How to create a Brazilian league pool at Bolão Elite</h2>
          <p>
            Go to the{' '}
            <a href="/en/brazilian-league-pool">Brazilian league pool</a> page,
            click &quot;Create pool&quot;, set the name, the privacy (private or
            public) and the scoring system. Since the season is long, I recommend
            Rule B (3 pts exact score, 1 pt winner/draw) — it balances rewarding
            the exact score with recognising the correct winner. Over 38
            matchdays, keeping things simple is worth more than a complicated
            rule. Those who prefer more granularity can opt for Rule C (10/5/2).
          </p>
          <p>
            For long pools, it pays to enable two features:
          </p>
          <ul>
            <li>
              <strong>Reminder notifications:</strong> with 380 matches,
              forgetting to predict is the most frequent mistake. A notification
              a few hours before the match prevents it.
            </li>
            <li>
              <strong>A matchday leaderboard on top of the overall one:</strong>{' '}
              in long championships, having a &quot;matchday highlight&quot;
              keeps the group engaged, even those doing poorly in the overall
              leaderboard.
            </li>
          </ul>

          <h2>Conclusion</h2>
          <p>
            A Brazilian league pool is a marathon, not a sprint. You can't get
            carried away in the first few matchdays and then sink into
            frustration by the 20th. Keep your rhythm, predict calmly, focus on
            getting the winner right and celebrate the exact scores when they
            come. At the end of the year, whoever predicts consistently lifts the
            pool trophy.
          </p>
          <p>
            For more tips, see{' '}
            <a href="/en/blog">all blog articles</a> or start with the{' '}
            <a href="/en/how-to-play">complete guide on how to play</a>.
            Technical terms such as &quot;goal difference&quot; or
            &quot;relegation&quot; are in the <a href="/en/glossary">glossary</a>.
          </p>
        </>
      ),
    },
    es: {
      slug: 'brasileirao-2026',
      title: 'Brasileirão 2026: tabla de posiciones, sistema de puntos y estrategia de pronósticos por jornada',
      excerpt:
        'Todos contra todos, 20 clubes, 38 jornadas y 380 partidos: el Brasileirão Serie A es el campeonato más maratónico del calendario. Mira cómo aprovecharlo en tu quiniela.',
      readTimeMinutes: 8,
      coverImageAlt: 'Portada del artículo: Brasileirão 2026 — tabla, puntos y estrategia',
      coverImage: '/blog/brasileirao-2026-es.svg',
      keywords: ['brasileirão 2026', 'serie a', 'tabla brasileirão', 'todos contra todos', 'quiniela brasileira'],
      content: (
        <>
          <p>
            El Campeonato Brasileño Serie A es el torneo nacional más largo, más
            denso y — para quien juega la quiniela — el más democrático del
            calendario del fútbol brasileño. A diferencia de la Copa de Brasil,
            donde una fase mala puede eliminarte con pocos partidos, el
            Brasileirão tiene 38 jornadas y 380 partidos. Hay margen para fallar
            bastante y aun así convertirte en campeón de la quiniela.
          </p>
          <p>
            En esta guía repasamos: el formato de todos contra todos, cómo
            funciona la tabla de posiciones, el criterio de clasificación para
            competiciones internacionales y — el foco central — cómo adaptar tu
            estrategia de pronósticos para una maratón de 9 meses.
          </p>

          <h2>El formato: todos contra todos, ida y vuelta</h2>
          <p>
            El Brasileirão Serie A funciona en formato de todos contra todos con
            20 clubes. Cada equipo juega contra todos los demás dos veces — una
            en casa, otra fuera —, sumando 38 jornadas. <strong>3 puntos por
            victoria, 1 por empate, 0 por derrota.</strong>
          </p>
          <p>
            El campeón es simplemente quien sume más puntos al final de las 38
            jornadas. Sin eliminatoria, sin definición a partido único — es
            resistencia pura.
          </p>

          <h3>Criterios de desempate</h3>
          <p>
            Cuando dos equipos empatan en puntos, el orden oficial de criterios
            es:
          </p>
          <ol>
            <li>Mayor número de victorias</li>
            <li>Mejor diferencia de goles</li>
            <li>Mayor número de goles marcados</li>
            <li>Enfrentamiento directo</li>
            <li>Menor número de tarjetas rojas + amarillas</li>
            <li>Sorteo</li>
          </ol>
          <p>
            La diferencia de goles, en la práctica, es el desempate más usado.
            Por eso, en la quiniela, conviene prestar atención a los marcadores
            abultados — pueden decidir no solo jornadas, sino el campeonato
            entero.
          </p>

          <h2>Lo que está en juego más allá del título</h2>
          <p>
            El Brasileirão no decide solo al campeón. Define también quién juega
            las competiciones continentales del año siguiente y quién desciende a
            la Serie B:
          </p>
          <ul>
            <li>
              <strong>Del 1.º al 6.º puesto:</strong> clasificados para la Copa
              Libertadores (4 cupos directos + 2 para la fase pre-Libertadores)
            </li>
            <li>
              <strong>Del 7.º al 12.º puesto:</strong> Copa Sudamericana
            </li>
            <li>
              <strong>Del 17.º al 20.º puesto:</strong> descendidos a la Serie B
            </li>
          </ul>
          <p>
            Estas franjas explican por qué <strong>todos los partidos importan
            hasta la última jornada</strong> — incluso los equipos de mitad de
            tabla juegan con el agua al cuello, peleando por un cupo
            internacional o por escapar del descenso.
          </p>

          <h2>Estrategia de pronósticos: maratón, no sprint</h2>
          <p>
            La gran diferencia entre el Brasileirão y torneos cortos como el
            Mundial es el aguante. Vas a pronosticar 380 partidos a lo largo de
            unos 9 meses. Estrategias que funcionan en una quiniela de Mundial{' '}
            <strong>no funcionan aquí</strong> — veamos por qué.
          </p>

          <h3>1. No intentes acertar todos los marcadores exactos</h3>
          <p>
            En 380 partidos, tu tasa de marcador exacto quedará probablemente
            entre el 7% y el 12% (incluso los pronosticadores profesionales rara
            vez superan el 15% a lo largo de una temporada entera). Fallar mucho
            es normal. Lo que marca la diferencia es <strong>mantener
            consistencia al acertar el ganador</strong>{' '}— los jugadores de
            quiniela de élite aciertan el ganador en torno al 55-60% de los
            partidos.
          </p>
          <p>
            Concentra tu esfuerzo en lo básico: acertar quién gana. El marcador
            exacto es un bono.
          </p>

          <h3>2. Conoce a los locales fuertes y a los visitantes flojos</h3>
          <p>
            En el fútbol brasileño, la ventaja de jugar en casa es mayor que en
            la mayoría de los países. Los equipos del interior, del Nordeste y
            del Sur tienen especialmente un peso grande en casa — su afición, el
            viaje agotador para el rival, las condiciones climáticas. <strong>Los
            equipos tradicionales de Río y São Paulo suelen rendir bien en casa,
            pero tienen campañas bastante más flojas como visitantes</strong>.
          </p>
          <p>
            Anota: <em>quien es fuerte en casa, es fuerte en casa — pero puede
            ser del montón como visitante</em>. Úsalo a la hora de pronosticar.
          </p>

          <h3>3. Cuidado con el cambio de vuelta</h3>
          <p>
            El Brasileirão tiene un ciclo característico: desde el inicio hasta la
            mitad de la primera vuelta, los equipos todavía se están acoplando —
            ves resultados extraños (sorpresas, goleadas inesperadas). A partir de
            la jornada 15, el campeonato &quot;decanta&quot;: los favoritos suben,
            los candidatos al descenso se hunden. La segunda vuelta es más
            previsible.
          </p>
          <p>
            Traducido al pronóstico: <strong>sé conservador en las 10 primeras
            jornadas</strong>. No intentes pronosticar resultados
            &quot;creativos&quot; al comienzo del campeonato. Espera a que los
            equipos muestren su identidad.
          </p>

          <h3>4. Atención al calendario paralelo</h3>
          <p>
            Los equipos brasileños disputan varios torneos simultáneamente:
            Brasileirão, Copa de Brasil, Libertadores o Copa Sudamericana, y los
            estatales (en el primer semestre). Esto afecta directamente a los
            pronósticos: <strong>un equipo puede usar a los suplentes en el
            Brasileirão para reservar a los titulares para un partido decisivo de
            la Libertadores la semana siguiente</strong>.
          </p>
          <p>
            Antes de pronosticar, échale un vistazo al calendario de los equipos
            grandes de tu jornada. Si uno de ellos tiene un partido decisivo de
            Libertadores la semana siguiente, es razonable esperar un equipo
            suplente — y ajustar tu pronóstico.
          </p>

          <h2>Cómo crear una quiniela del Brasileirão en Bolão Elite</h2>
          <p>
            Entra en la página{' '}
            <a href="/es/quiniela-brasileira">quiniela del Brasileirão</a>, haz
            clic en &quot;Crear quiniela&quot;, define el nombre, la privacidad
            (privada o pública) y el sistema de puntuación. Como la temporada es
            larga, recomiendo la Regla B (3 pts marcador exacto, 1 pt
            ganador/empate) — equilibra la recompensa del acierto exacto con el
            reconocimiento del ganador correcto. En 38 jornadas, mantener algo
            simple vale más que una regla complicada. Quien prefiere más
            granularidad puede optar por la Regla C (10/5/2).
          </p>
          <p>
            Para quinielas largas, conviene habilitar dos funcionalidades:
          </p>
          <ul>
            <li>
              <strong>Notificaciones de recordatorio:</strong> con 380 partidos,
              olvidarse de pronosticar es el error más frecuente. Una
              notificación unas horas antes del partido lo evita.
            </li>
            <li>
              <strong>Clasificación por jornada además de la general:</strong> en
              campeonatos largos, tener un &quot;destacado de la jornada&quot;
              mantiene a la gente enganchada, incluso a quien va mal en la
              clasificación general.
            </li>
          </ul>

          <h2>Conclusión</h2>
          <p>
            Una quiniela del Brasileirão es una maratón, no un sprint. No puedes
            entusiasmarte en las primeras jornadas y luego caer en la frustración
            en la 20.ª. Mantén el ritmo, pronostica con calma, enfócate en
            acertar el ganador y celebra los marcadores exactos cuando lleguen. A
            fin de año, quien pronostica con consistencia levanta el trofeo de la
            quiniela.
          </p>
          <p>
            Para más consejos, mira{' '}
            <a href="/es/blog">todos los artículos del blog</a> o empieza por la{' '}
            <a href="/es/como-jugar">guía completa de cómo jugar</a>. Términos
            técnicos como &quot;diferencia de goles&quot; o &quot;descenso&quot;
            están en el <a href="/es/glosario">glosario</a>.
          </p>
        </>
      ),
    },
  },
};
